"use client";

import { Box } from "@mui/material";
import type { BodyPartType } from "@rmh/defs";
import type { KeyboardEvent } from "react";

import type { BodyFigurePathDef } from "./bodyFigurePaths";
import { getPathSx, outlinePathSx } from "./bodyFigureStyles";
import type { OnboardingBodyFigureLabels } from "./labels";

interface BodyFigureSvgProps {
  viewBox: string;
  paths: readonly BodyFigurePathDef[];
  outlineD: string;
  selectedPart: BodyPartType | null;
  onPartClick: (part: BodyPartType) => void;
  ariaLabel: string;
  partLabels: OnboardingBodyFigureLabels["parts"];
  dataCyPrefix: string;
}

export const BodyFigureSvg = ({
  viewBox,
  paths,
  outlineD,
  selectedPart,
  onPartClick,
  ariaLabel,
  partLabels,
  dataCyPrefix,
}: BodyFigureSvgProps) => {
  const handleKeyDown =
    (part: BodyPartType) => (event: KeyboardEvent<SVGPathElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onPartClick(part);
      }
    };

  return (
    <Box
      component="svg"
      viewBox={viewBox}
      role="group"
      aria-label={ariaLabel}
      sx={{
        width: "100%",
        height: "100%",
        display: "block",
        overflow: "visible",
      }}
    >
      {paths.map((p) => {
        const isInteractive = p.part !== null;
        const isSelected = p.part !== null && p.part === selectedPart;
        return (
          <Box
            key={`${dataCyPrefix}__${p.labelKey}`}
            component="path"
            d={p.d}
            data-cy={`${dataCyPrefix}__${p.labelKey}`}
            data-selected={isSelected || undefined}
            role={isInteractive ? "button" : "presentation"}
            tabIndex={isInteractive ? 0 : -1}
            aria-pressed={isInteractive ? isSelected : undefined}
            aria-label={isInteractive ? partLabels[p.labelKey] : undefined}
            aria-hidden={isInteractive ? undefined : true}
            onClick={
              isInteractive && p.part ? () => onPartClick(p.part!) : undefined
            }
            onKeyDown={
              isInteractive && p.part ? handleKeyDown(p.part) : undefined
            }
            sx={getPathSx({ isSelected, isInteractive })}
          />
        );
      })}
      <Box
        component="path"
        d={outlineD}
        aria-hidden={true}
        sx={outlinePathSx}
      />
    </Box>
  );
};
